import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { useOperationVerificationApplications } from '@/hooks/useOperationVerificationApplications'
import { formatDate } from '@/lib/venue/format'
import { operationVerificationReviewedAtText } from '@/lib/venue/operationVerificationStatusViewModel'
import type { OperationVerificationApplicationRow } from '@/lib/venue/operationVerificationTypes'
import { useVenueContext } from '@/routes/venueContext'

/**
 * 운영 인증 이력 — 과거 인증 회차와 처리 결과 조회(요구사항 3, `#778`, 백엔드 `#777` §3).
 *
 * 🚨 `outcome`/`outcomeLabel`을 서버가 준 그대로 렌더한다 — 재인증으로 대체된 옛 회차도 `EXPIRED`로
 * 내려오며, 클라이언트가 `verifiedUntil`을 자기 시계와 비교해 다시 계산하지 않는다(api-spec §3-2).
 */
export function VenueOperationVerificationHistoryPage() {
  const { venueId } = useVenueContext()
  const applicationsQuery = useOperationVerificationApplications(venueId)

  const rows = applicationsQuery.data?.pages.flatMap((page) => page.applications) ?? []

  return (
    <>
      <PageHeader title="운영 인증 이력" description="과거 인증 회차와 처리 결과를 확인합니다." />

      {applicationsQuery.isLoading ? <LoadingState rows={4} label="인증 이력을 불러오는 중" /> : null}

      {applicationsQuery.isError ? (
        <ErrorState
          error={applicationsQuery.error}
          onRetry={() => {
            void applicationsQuery.refetch()
          }}
        />
      ) : null}

      {applicationsQuery.data ? (
        rows.length === 0 ? (
          <EmptyState title="인증 신청 이력이 없습니다" />
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <ApplicationRow key={row.verificationId} row={row} />
            ))}

            {applicationsQuery.hasNextPage ? (
              <div className="flex justify-center">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={applicationsQuery.isFetchingNextPage}
                  onClick={() => {
                    void applicationsQuery.fetchNextPage()
                  }}
                >
                  {applicationsQuery.isFetchingNextPage ? '불러오는 중…' : '더 보기'}
                </Button>
              </div>
            ) : null}
          </div>
        )
      ) : null}
    </>
  )
}

function ApplicationRow({ row }: { row: OperationVerificationApplicationRow }) {
  return (
    <Card>
      <CardBody className="space-y-1">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-ink-700">{row.outcomeLabel}</span>
          <span className="text-xs text-slateish-500">{formatDate(row.submittedAt)} 신청</span>
        </div>
        <p className="text-xs text-slateish-600">
          {operationVerificationReviewedAtText(row.reviewedAt)}
          {row.reviewerTypeLabel ? ` · ${row.reviewerTypeLabel}` : ''}
        </p>
        {row.reason ? <p className="text-xs text-danger-500">{row.reason}</p> : null}
      </CardBody>
    </Card>
  )
}
